import { addDaysToBaliKey, diffBaliDays, getBaliDateKey, netMembershipPrice, membershipStillPaid, type RefundTotals } from '@/lib';
import { baliMonthKey, baliWeekStart, dayLabel, monthLabel, nextMonthKey, weekLabel, weekStartOfKey } from '@/utils/reports/date-bucketing';

/**
 * Membership cohort analytics for the Memberships report: every membership
 * sold, assigned to one of five length cohorts (Daily / Weekly / Monthly /
 * Quarterly / Yearly), bucketed by the Bali day, week or month it was SOLD in.
 *
 * Two things separate this from the "Membership Split" pie next to it:
 *
 *  - the pie is a single all-time snapshot; this is a time series, so it can
 *    answer "is the mix shifting toward yearly?" rather than only "what is the
 *    mix?";
 *  - the pie classifies purely by date span, which is wrong for a
 *    freeze-extended row — a 12-week membership frozen for three months spans
 *    182 days and falls in the pie's excluded 121–299 day gap, disappearing
 *    from the mix entirely. Here the PLAN NAME is consulted first (see
 *    `cohortOfMembership`), and the span is only a fallback.
 */

export type MembershipCohortKey =
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'yearly'
  | 'other';

/** How a membership's cohort was decided — surfaced in the report's explainer. */
export type CohortMatchSource = 'name' | 'duration' | 'none';

export type CohortGrain = 'day' | 'week' | 'month';
export type CohortMetric = 'count' | 'revenue';
/** `paid` = money actually stayed with the club; `all` = comps and trials too. */
export type CohortScope = 'paid' | 'all';

/**
 * The cohorts in fixed presentation order — also the bottom-up stack order and
 * the CSV column order.
 *
 * Colours are the SAME five hues the Membership Split pie uses for the same
 * five lengths, so a cohort keeps one identity across both charts on this tab.
 * The order below is the validated adjacent-pair order (worst adjacent pair
 * ΔE 14.7 under protanopia, well above the ≥8 floor). `other` is deliberately
 * a neutral rather than a sixth hue: it is the unclassified catch-all, not an
 * identity of its own.
 *
 * Colour is bound to the cohort KEY, never to its rank or its position in the
 * filtered set — deselecting Monthly must not repaint Quarterly.
 */
export const MEMBERSHIP_COHORTS: ReadonlyArray<{
  key: MembershipCohortKey;
  name: string;
  color: string;
}> = [
  { key: 'daily', name: 'Daily', color: '#F59E0B' },
  { key: 'weekly', name: 'Weekly', color: '#3B82F6' },
  { key: 'monthly', name: 'Monthly', color: '#EC4899' },
  { key: 'quarterly', name: 'Quarterly', color: '#8B5CF6' },
  { key: 'yearly', name: 'Yearly', color: '#10B981' },
  { key: 'other', name: 'Other', color: '#9CA3AF' },
];

export const COHORT_KEYS: readonly MembershipCohortKey[] = MEMBERSHIP_COHORTS.map((c) => c.key);

const COHORT_NAMES: Record<MembershipCohortKey, string> = Object.fromEntries(
  MEMBERSHIP_COHORTS.map((c) => [c.key, c.name]),
) as Record<MembershipCohortKey, string>;

export const cohortName = (key: MembershipCohortKey): string => COHORT_NAMES[key];

/** One membership row, as fetched by `useMembershipCohorts`. */
export interface MembershipCohortRow {
  id: string;
  /** Plan-name snapshot taken at sale time (`memberships.name`). */
  name: string | null;
  /** `numeric` column — arrives from PostgREST as a STRING. */
  price: number | string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
}

/**
 * Length buckets in days. Boundaries are the Membership Split pie's, unchanged,
 * so the two charts agree on what "monthly" means: Daily 0–1, Weekly 2–20,
 * Monthly 21–69, Quarterly 70–120, Yearly 300–430. Lengths in the gaps
 * (121–299, 431+) are `other` here rather than dropped — a report that filters
 * by cohort has to account for every row it fetched, or the stack silently
 * stops summing to the total.
 *
 * Inclusive on both ends; must not overlap.
 */
const LENGTH_BUCKETS: ReadonlyArray<{
  key: MembershipCohortKey;
  minDays: number;
  maxDays: number;
}> = [
  { key: 'daily', minDays: 0, maxDays: 1 },
  { key: 'weekly', minDays: 2, maxDays: 20 },
  { key: 'monthly', minDays: 21, maxDays: 69 },
  { key: 'quarterly', minDays: 70, maxDays: 120 },
  { key: 'yearly', minDays: 300, maxDays: 430 },
];

function bucketOfDays(days: number): MembershipCohortKey {
  if (!Number.isFinite(days)) return 'other';
  return LENGTH_BUCKETS.find((b) => days >= b.minDays && days <= b.maxDays)?.key ?? 'other';
}

/** Days per unit for a "<n> <unit>" plan name. Months/years are nominal. */
const UNIT_DAYS: Record<string, number> = {
  day: 1,
  week: 7,
  month: 30,
  year: 365,
};

/**
 * A quantified length in the plan name: "4 Weeks", "1 Week Free Membership",
 * "12 Weeks", "6-Month". Plural `s` and a hyphen are both optional.
 */
const QUANTIFIED_LENGTH = /(\d+)\s*-?\s*(day|week|month|year)s?\b/i;

/**
 * Unquantified length words, checked only when no number is present. Ordered
 * longest-first where one word is a prefix of another so `daily` can never be
 * shadowed. Each maps to the canonical length of that plan in days.
 */
const NAMED_LENGTHS: ReadonlyArray<[RegExp, number]> = [
  [/\bday\s*pass\b/i, 1],
  [/\bdaily\b/i, 1],
  [/\bweekly\b/i, 7],
  [/\bfortnight/i, 14],
  [/\bmonthly\b/i, 30],
  [/\bquarterly\b/i, 90],
  [/\bquarter\b/i, 90],
  [/\b(?:yearly|annual|annually)\b/i, 360],
];

/** Days a plan name declares itself to last, or `null` when it says nothing. */
function declaredLengthDays(name: string | null): number | null {
  if (!name) return null;
  const quantified = QUANTIFIED_LENGTH.exec(name);
  if (quantified) {
    const count = Number(quantified[1]);
    const unit = UNIT_DAYS[quantified[2].toLowerCase()];
    if (Number.isFinite(count) && unit) return count * unit;
  }
  for (const [pattern, days] of NAMED_LENGTHS) {
    if (pattern.test(name)) return days;
  }
  return null;
}

/**
 * Which cohort a membership belongs to.
 *
 * **The plan name wins over the dates**, and that ordering is the whole point.
 * A membership's `end_date` is not a description of what was sold: freezes push
 * it out (a 12-week plan frozen twice spans half a year) and admins edit it by
 * hand. The name snapshot is written once at sale time and never moves, so it
 * is the only field that still says what the member actually bought. The span
 * is consulted only when the name declares no length at all — which today means
 * the comp plans ("Friends & Family", "Adidas"), where the span IS the best
 * available signal.
 *
 * `matchedBy` is reported so the UI can say how many rows fell through to the
 * span, rather than presenting a guess as a fact.
 */
export function cohortOfMembership(row: MembershipCohortRow): {
  key: MembershipCohortKey;
  matchedBy: CohortMatchSource;
} {
  const declared = declaredLengthDays(row.name);
  if (declared !== null) {
    const key = bucketOfDays(declared);
    // A named length that lands in a boundary gap (e.g. "6 Months") is still a
    // name match — it is genuinely none of the five cohorts, not an unknown.
    return { key, matchedBy: 'name' };
  }

  const span =
    row.start_date && row.end_date ? diffBaliDays(row.start_date, row.end_date) : NaN;
  if (!Number.isFinite(span)) return { key: 'other', matchedBy: 'none' };
  return { key: bucketOfDays(span), matchedBy: 'duration' };
}

/**
 * The rows a given scope counts.
 *
 * `paid` uses `membershipStillPaid`, not a bare `price > 0`: a membership
 * refunded in full was paid for and then wasn't, and counting it leaves a
 * reversed sale in the mix. `all` counts every row — comps, free trials and
 * Friends & Family included — which on production is roughly five times the
 * paid volume, hence the report's explicit scope control rather than a
 * hard-coded choice.
 */
export function filterCohortRows(
  rows: MembershipCohortRow[],
  scope: CohortScope,
  refundTotals: RefundTotals,
): MembershipCohortRow[] {
  if (scope === 'all') return rows;
  return rows.filter((r) => membershipStillPaid(r.id, r.price, refundTotals));
}

export interface CohortTotals {
  count: number;
  /** Net of refunds — see `netMembershipPrice`. */
  revenue: number;
}

export interface MembershipCohortSummary {
  byCohort: Record<MembershipCohortKey, CohortTotals>;
  totalCount: number;
  totalRevenue: number;
  /** Rows whose cohort came from the date span because the name was silent. */
  derivedFromDuration: number;
  /** Rows with no usable name AND no usable dates — always in `other`. */
  unclassified: number;
}

const emptyTotals = (): Record<MembershipCohortKey, CohortTotals> =>
  Object.fromEntries(COHORT_KEYS.map((k) => [k, { count: 0, revenue: 0 }])) as Record<
    MembershipCohortKey,
    CohortTotals
  >;

/**
 * Per-cohort counts and revenue over the whole fetched set. Always covers ALL
 * six cohorts regardless of the UI's cohort filter — the breakdown table and
 * the filter chips both need the unfiltered figures to show what selecting a
 * cohort would include.
 */
export function summarizeMembershipCohorts(
  rows: MembershipCohortRow[],
  refundTotals: RefundTotals,
): MembershipCohortSummary {
  const byCohort = emptyTotals();
  let derivedFromDuration = 0;
  let unclassified = 0;

  for (const row of rows) {
    const { key, matchedBy } = cohortOfMembership(row);
    if (matchedBy === 'duration') derivedFromDuration++;
    if (matchedBy === 'none') unclassified++;
    byCohort[key].count++;
    byCohort[key].revenue += netMembershipPrice(row.id, row.price, refundTotals);
  }

  return {
    byCohort,
    totalCount: COHORT_KEYS.reduce((sum, k) => sum + byCohort[k].count, 0),
    totalRevenue: COHORT_KEYS.reduce((sum, k) => sum + byCohort[k].revenue, 0),
    derivedFromDuration,
    unclassified,
  };
}

/** One period on the x-axis. Cohort keys carry the metric's value. */
export type MembershipCohortPoint = Record<MembershipCohortKey, number> & {
  /**
   * `YYYY-MM-DD` for day grain (the Bali date) and week grain (the Monday),
   * `YYYY-MM` for month grain.
   */
  bucket: string;
  label: string;
  /** Sum of the SELECTED cohorts only, so the total line matches the stack. */
  total: number;
};

export interface CohortSeriesOptions {
  grain: CohortGrain;
  metric: CohortMetric;
  /** Cohorts the user has left switched on. */
  selected: readonly MembershipCohortKey[];
  refundTotals: RefundTotals;
  now?: Date;
  /**
   * Bali date key (YYYY-MM-DD) pinning the left edge of the series — the report
   * passes `SOFT_OPENING_DATE_KEY`, as the Active Members and MRR charts do.
   * Sales on earlier Bali days are NOT drawn (unlike coverage, a pre-opening
   * sale clipped into the first bar would fake a day-one spike), and the axis
   * starts at the period containing this day even when the first sale is later.
   */
  floorDay?: string;
}

/**
 * Caps on emitted periods (~2 years of days, 10 years of weeks, 20 years of
 * months). The window is anchored to the NEWEST period, so one corrupt ancient
 * `created_at` can never push current data off the chart — older rows simply
 * fall outside the window instead of stretching it. The day cap is the
 * tightest: past two years, ~700 stacked bars are unreadable anyway, and the
 * month grain is the right tool for that range.
 */
const MAX_PERIODS: Record<CohortGrain, number> = {
  day: 730,
  week: 520,
  month: 240,
};

/** Period key containing an instant, at the requested grain. */
const bucketOf = (grain: CohortGrain, d: Date): string => {
  if (grain === 'day') return getBaliDateKey(d);
  return grain === 'week' ? baliWeekStart(d) : baliMonthKey(d);
};

/** Period key containing a YYYY-MM-DD Bali date key, at the requested grain. */
const bucketOfDayKey = (grain: CohortGrain, dayKey: string): string => {
  if (grain === 'day') return dayKey;
  return grain === 'week' ? weekStartOfKey(dayKey) : dayKey.slice(0, 7);
};

/** The next period key after `bucket`. */
const nextBucket = (grain: CohortGrain, bucket: string): string => {
  if (grain === 'month') return nextMonthKey(bucket);
  return addDaysToBaliKey(bucket, grain === 'week' ? 7 : 1);
};

/** Human label for a period key. */
const bucketLabel = (grain: CohortGrain, bucket: string): string => {
  if (grain === 'day') return dayLabel(bucket);
  return grain === 'week' ? weekLabel(bucket) : monthLabel(bucket);
};

/**
 * Cohort mix over time, one point per Bali day, Mon–Sun Bali week or calendar
 * month.
 *
 * Bucketed on `created_at` — when the membership was SOLD, not when it starts.
 * During pre-sales a membership is bought months before it activates, and this
 * report is about sales mix, so the purchase date is the event. (Contrast
 * `buildActiveMemberGrowth`, which counts a member on the days their membership
 * actually covers — it asks who is a member, not what was sold when.)
 *
 * Every period between the first and last sale is emitted, including empty ones
 * — a gap-free axis is what makes a stacked chart readable as a rate. The
 * series runs to the current period or the newest data period, whichever is
 * later, so a row created a moment ago is never dropped by a lagging clock.
 * With `floorDay` set, the left edge is the period containing that day instead,
 * and earlier sales are dropped from the series entirely (see the option doc).
 */
export function buildMembershipCohortSeries(
  rows: MembershipCohortRow[],
  { grain, metric, selected, refundTotals, now = new Date(), floorDay }: CohortSeriesOptions,
): MembershipCohortPoint[] {
  if (rows.length === 0) return [];

  // bucket -> cohort -> metric value
  const grid = new Map<string, Record<MembershipCohortKey, number>>();
  for (const row of rows) {
    const created = new Date(row.created_at);
    if (Number.isNaN(created.getTime())) continue;
    if (floorDay && getBaliDateKey(created) < floorDay) continue;
    const bucket = bucketOf(grain, created);
    let cell = grid.get(bucket);
    if (!cell) {
      cell = Object.fromEntries(COHORT_KEYS.map((k) => [k, 0])) as Record<
        MembershipCohortKey,
        number
      >;
      grid.set(bucket, cell);
    }
    const { key } = cohortOfMembership(row);
    cell[key] +=
      metric === 'count' ? 1 : netMembershipPrice(row.id, row.price, refundTotals);
  }
  if (grid.size === 0) return [];

  const sorted = [...grid.keys()].sort();
  const lastData = sorted[sorted.length - 1];
  const current = bucketOf(grain, now);
  const endBucket = lastData > current ? lastData : current;

  // Left edge of the series: the first data period, or with a floor set, the
  // period CONTAINING the floor day — even when the first sale lands later, so
  // the axis starts at soft opening with zero periods in between, matching the
  // MRR and Active Members charts.
  const firstData = floorDay ? bucketOfDayKey(grain, floorDay) : sorted[0];

  // Walk back from the end to find the earliest period the cap allows. Stepping
  // rather than arithmetic keeps every grain on one code path (and month steps
  // aren't a fixed number of days).
  const maxPeriods = MAX_PERIODS[grain];
  let startBucket = endBucket;
  for (let i = 1; i < maxPeriods && startBucket > firstData; i++) {
    const prev = previousBucket(grain, startBucket);
    if (prev < firstData) break;
    startBucket = prev;
  }

  const selectedSet = new Set(selected);
  const points: MembershipCohortPoint[] = [];
  for (let bucket = startBucket; bucket <= endBucket; bucket = nextBucket(grain, bucket)) {
    const cell = grid.get(bucket);
    const point = {
      bucket,
      label: bucketLabel(grain, bucket),
      total: 0,
    } as MembershipCohortPoint;
    for (const key of COHORT_KEYS) {
      const value = cell?.[key] ?? 0;
      point[key] = value;
      if (selectedSet.has(key)) point.total += value;
    }
    points.push(point);
  }
  return points;
}

/**
 * The period before `bucket`. For months this steps back through
 * `nextMonthKey` on a January anchor rather than duplicating its rollover
 * arithmetic in reverse.
 */
function previousBucket(grain: CohortGrain, bucket: string): string {
  if (grain !== 'month') return addDaysToBaliKey(bucket, grain === 'week' ? -7 : -1);
  const [y, m] = bucket.split('-').map(Number);
  const total = y * 12 + (m - 1) - 1;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}
